'use client';

import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/context/LanguageContext';

/**
 * Sección "Alcance comercial + Entrega en los Estados Unidos".
 *
 * Sistema de color acordado con José el 26-ago-2026:
 *   · TURQUESA (#00c8d4) = color institucional — etiquetas, palabras clave, sistema.
 *   · DORADO   (#C9A84C) = marcador de énfasis — marca lo que el ojo tiene que ir a buscar.
 *     Para TEXTO se usa el dorado aclarado (#E3C878): el #C9A84C no tiene contraste
 *     suficiente en cuerpo chico sobre fondo oscuro.
 *   · Regla práctica: no más de dos o tres marcas doradas por pantalla, o deja de resaltar.
 */

const GOLD = '#C9A84C';
const GOLD_TEXT = '#E3C878';

export default function GlobalReachDelivery() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries?.forEach((entry) => {
          if (entry?.isIntersecting) {
            entry?.target?.querySelectorAll('.reveal')?.forEach((el) => el?.classList?.remove('hidden-init'));
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  const markets = ['reach.market.0', 'reach.market.1', 'reach.market.2', 'reach.market.3', 'reach.market.4', 'reach.market.5'];

  return (
    <section
      ref={sectionRef}
      className="relative py-20 lg:py-28 overflow-hidden"
      style={{ background: 'linear-gradient(155deg,#0d1a27 0%,#152b40 46%,#10333f 100%)' }}
    >
      {/* resplandor turquesa */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(880px 400px at 84% 6%, rgba(0,200,212,0.13), transparent 66%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">

        {/* ── Bloque 1 · Alcance global ───────────────────────────── */}
        <div className="reveal hidden-init pl-6 lg:pl-7 mb-8" style={{ borderLeft: `5px solid ${GOLD}` }}>
          <span className="block text-xs font-bold uppercase tracking-[0.18em] mb-3.5 text-secondary-bright">
            {t('reach.label')}
          </span>
          <h2
            className="font-display text-3xl lg:text-[44px] font-bold text-white"
            style={{ letterSpacing: '-0.025em', lineHeight: 1.06, maxWidth: '19ch' }}
          >
            {t('reach.title')}
          </h2>
        </div>

        <p className="text-white/75 text-lg leading-relaxed mb-7 reveal hidden-init stagger-1" style={{ maxWidth: '66ch' }}>
          {t('reach.lead')}
        </p>

        {/* fichas de mercado — enmarcadas en dorado */}
        <div className="flex flex-wrap gap-2.5 mb-7 reveal hidden-init stagger-2">
          {markets?.map((key) => (
            <span
              key={key}
              className="text-white font-semibold rounded-xl"
              style={{
                fontSize: '13.5px',
                padding: '10px 17px',
                background: 'rgba(201,168,76,0.07)',
                border: `1.5px solid ${GOLD}`,
                boxShadow: '0 0 0 1px rgba(201,168,76,0.10), 0 6px 18px rgba(0,0,0,0.18)',
              }}
            >
              {t(key)}
            </span>
          ))}
        </div>

        <p className="text-white/55 text-sm leading-relaxed mb-2.5 reveal hidden-init stagger-3" style={{ maxWidth: '76ch' }}>
          {t('reach.quoting')}
        </p>
        <p className="text-white/55 text-sm leading-relaxed reveal hidden-init stagger-3" style={{ maxWidth: '76ch' }}>
          {t('reach.caveat')}
        </p>

        {/* ── Bloque 2 · Estados Unidos ───────────────────────────── */}
        <div
          className="mt-12 lg:mt-14 rounded-[20px] p-8 lg:p-11 reveal hidden-init stagger-4"
          style={{
            background: 'linear-gradient(135deg,#136070 0%,#0E3A60 100%)',
            boxShadow: '0 24px 55px rgba(0,0,0,0.38)',
          }}
        >
          <span className="block text-xs font-bold uppercase tracking-[0.18em] mb-3.5" style={{ color: '#7fe6ee' }}>
            {t('ior.label')}
          </span>
          <h3
            className="font-display text-2xl lg:text-[33px] font-bold text-white mb-5"
            style={{ letterSpacing: '-0.02em', lineHeight: 1.1, maxWidth: '24ch' }}
          >
            {t('ior.title')}
          </h3>

          <p className="text-white/85 leading-relaxed mb-3.5" style={{ fontSize: '15px', maxWidth: '70ch' }}>
            {t('ior.p1')}
          </p>
          <p className="text-white/85 leading-relaxed mb-7" style={{ fontSize: '15px', maxWidth: '70ch' }}>
            {t('ior.p2')}
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-7">
            {/* DAP — recomendada, blanca y enmarcada en dorado */}
            <div
              className="rounded-2xl p-6 bg-white"
              style={{ border: `2px solid ${GOLD}`, boxShadow: '0 0 0 4px rgba(201,168,76,0.22), 0 16px 38px rgba(0,0,0,0.30)' }}
            >
              <div className="font-display text-[23px] font-bold mb-1.5 text-primary">
                DAP
                <span
                  className="inline-block align-middle ml-2.5 text-[10px] font-bold uppercase tracking-[0.12em] rounded-full"
                  style={{ padding: '4px 10px', background: GOLD, color: '#241c05' }}
                >
                  {t('ior.dap.badge')}
                </span>
              </div>
              <div className="text-sm font-bold text-primary mb-2">{t('ior.dap.title')}</div>
              <p className="text-[13px] leading-relaxed m-0" style={{ color: '#4a5f6d' }}>{t('ior.dap.body')}</p>
            </div>

            {/* DDP */}
            <div
              className="rounded-2xl p-6"
              style={{ background: 'rgba(6,22,30,0.34)', border: '1px solid rgba(255,255,255,0.16)' }}
            >
              <div className="font-display text-[23px] font-bold mb-1.5 text-white/80">DDP</div>
              <div className="text-sm font-bold text-white mb-2">{t('ior.ddp.title')}</div>
              <p className="text-[13px] leading-relaxed m-0 text-white/70">{t('ior.ddp.body')}</p>
            </div>
          </div>

          <p className="text-white/85 text-sm leading-relaxed" style={{ maxWidth: '76ch' }}>
            {t('ior.recommendation.a')}{' '}
            <strong className="font-bold" style={{ color: GOLD_TEXT }}>{t('ior.recommendation.b')}</strong>{' '}
            {t('ior.recommendation.c')}
          </p>
        </div>

      </div>
    </section>
  );
}
